import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Select, SelectContent, SelectItem } from '@dextech-ai/dex-design-system';
import { filterByLabel } from '@/utils/select-items';

/**
 * Rendered in place of a normal row when a rule references a field that no
 * longer exists in the schema (after rename/delete). Red-tinted, disabled,
 * offers Replace-with... picker + Remove. Save is blocked until all ghost
 * rows are resolved (validateRules emits dangling_field errors for them).
 */
export default function DanglingRefRow({
	scope,
	ruleIndex,
	condIndex,
	missingName,
	compatibleFields,
	onReplace,
	onRemove,
}) {
	const [picker, setPicker] = useState(null);
	const testIdBase = condIndex != null
		? `xallof-rule-${ruleIndex}-${scope}-${condIndex}`
		: `xallof-rule-${ruleIndex}-${scope}-${missingName}`;

	return (
		<div
			className="dsv-xallof-builder__ghost-row"
			role="group"
			aria-label={`Dangling reference to ${missingName}`}
			data-testid={`${testIdBase}-ghost`}
		>
			<span className="dsv-xallof-builder__ghost-label">
				Field <code>{missingName}</code> no longer exists.
			</span>
			<div className="dsv-xallof-builder__ghost-picker">
				<Select
					size="sm"
					value={picker}
					onChange={setPicker}
					placeholder="Replace with..."
					showSearch
					filterOption={filterByLabel}
					popupMatchSelectWidth={false}
					data-testid={`${testIdBase}-replace-picker`}
				>
					<SelectContent>
						{compatibleFields.map((f) => {
							// Back-compat: earlier callers passed entries with dataElementName;
							// Phase 1b passes addressable entries with displayPath.
							const key = f.displayPath || f.dataElementName;
							return (
								<SelectItem key={key} value={key}>
									{key}
								</SelectItem>
							);
						})}
					</SelectContent>
				</Select>
			</div>
			<button
				type="button"
				className="dsv-btn dsv-btn--secondary dsv-btn--sm"
				disabled={!picker}
				onClick={() => {
					if (picker) onReplace(picker);
				}}
				data-testid={`${testIdBase}-replace`}
			>
				Replace
			</button>
			<button
				type="button"
				className="dsv-btn dsv-btn--danger dsv-btn--sm"
				onClick={onRemove}
				data-testid={`${testIdBase}-remove`}
			>
				Remove
			</button>
		</div>
	);
}

DanglingRefRow.propTypes = {
	scope: PropTypes.string.isRequired,
	ruleIndex: PropTypes.number.isRequired,
	condIndex: PropTypes.number,
	missingName: PropTypes.string.isRequired,
	compatibleFields: PropTypes.array.isRequired,
	onReplace: PropTypes.func.isRequired,
	onRemove: PropTypes.func.isRequired,
};
